import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { api } from '../../lib/api';
import { Field, TextInput, TextArea, Select, MoneyInput, History, Empty, Money, fmtDate, btnCls, btn2Cls, cardCls, CLAIM_STATUS_LABEL, FEE_EFFECT_LABEL, APPROVER_STATUSES, StatusBadge } from './shared';

// Refunds, returns and customer claims: what was claimed, who funds it (CCS vs Concria),
// the invoice it relates to, and its effect on partner fees. The fee effect is a documented
// field only; the Books payout calculator is not changed by it.

const VARIANT = { open: 'info', disputed: 'warning', agreed: 'success', settled: 'primary', written_off: 'default', closed: 'default' };

export default function ClaimsTab({ me, deals, onChanged }) {
  const [claims, setClaims] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setClaims((await api.get('/intercompany/claims')).claims); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setOpen(null); load(); onChanged(); };
  const m = (c) => (c == null ? <span className="text-[var(--text-secondary)]">not recorded</span> : <Money cents={c} />);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button className={btnCls} onClick={() => setOpen({})}><Plus size={14} /> Add claim</button></div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!claims ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p> : claims.length === 0 ? <Empty>No claims recorded.</Empty> : claims.map((c) => (
        <button key={c.id} onClick={() => setOpen(c)} className={`${cardCls} block w-full text-left hover:border-primary`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-[var(--text-primary)]">{c.title}</div>
              <div className="text-xs text-[var(--text-secondary)]">{[c.customer, c.project, c.claim_date && fmtDate(c.claim_date), c.original_invoice_ref].filter(Boolean).join(' · ')}</div>
            </div>
            <Badge variant={VARIANT[c.status]}>{CLAIM_STATUS_LABEL[c.status]}</Badge>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
            <div><dt className="text-xs text-[var(--text-secondary)]">Claimed</dt><dd>{m(c.claim_cents)}</dd></div>
            <div><dt className="text-xs text-[var(--text-secondary)]">Refund paid</dt><dd>{m(c.refund_paid_cents)}</dd></div>
            <div><dt className="text-xs text-[var(--text-secondary)]">CCS share</dt><dd>{m(c.ccs_share_cents)}</dd></div>
            <div><dt className="text-xs text-[var(--text-secondary)]">Concria share</dt><dd>{m(c.concria_share_cents)}</dd></div>
            <div><dt className="text-xs text-[var(--text-secondary)]">Concria paid</dt><dd>{m(c.concria_paid_cents)}</dd></div>
          </dl>
          {c.reason && <p className="mt-2 text-xs text-[var(--text-secondary)]">{c.reason}</p>}
          <p className="mt-1 text-xs">Fees: {FEE_EFFECT_LABEL[c.fee_effect]}</p>
          {c.lines.length > 0 && <div className="mt-2 flex flex-wrap gap-2 text-xs">{c.lines.map((l) => <span key={l.id} className="inline-flex items-center gap-1">{l.description.slice(0, 40)} <StatusBadge status={l.status} /></span>)}</div>}
        </button>
      ))}
      {open && <ClaimModal claim={open.id ? open : null} me={me} deals={deals} onClose={() => setOpen(null)} onSaved={done} />}
    </div>
  );
}

const FIELDS = ['title', 'customer', 'project', 'claim_date', 'reason', 'original_invoice_ref', 'original_invoice_cents', 'claim_cents', 'refund_paid_cents',
  'ccs_share_cents', 'concria_share_cents', 'concria_paid_cents', 'status', 'fee_effect', 'fee_effect_note', 'books_deal_id', 'source_url', 'note'];
const MONEY = [['original_invoice_cents', 'Original invoice'], ['claim_cents', 'Amount claimed'], ['refund_paid_cents', 'Refund paid'], ['ccs_share_cents', 'CCS share'], ['concria_share_cents', 'Concria share'], ['concria_paid_cents', 'Concria has paid']];

function ClaimModal({ claim, me, deals, onClose, onSaved }) {
  const [f, setF] = useState(() => (claim ? Object.fromEntries(FIELDS.map((k) => [k, claim[k] ?? ''])) : { status: 'open', fee_effect: 'undecided' }));
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const statusOptions = Object.fromEntries(Object.entries(CLAIM_STATUS_LABEL).filter(([k]) => me.can_approve || !APPROVER_STATUSES.includes(k) || k === claim?.status));
  const save = async () => {
    try {
      const body = {};
      for (const k of FIELDS) {
        const v = f[k];
        if (claim && String(v ?? '') === String(claim[k] ?? '')) continue;
        if (!claim && (v === '' || v == null)) continue;
        body[k] = v === '' ? null : v;
      }
      if (claim) await api.patch(`/intercompany/claims/${claim.id}`, { ...body, revision: claim.updated_at });
      else await api.post('/intercompany/claims', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this claim?')) return;
    try { await api.delete(`/intercompany/claims/${claim.id}`); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={claim ? 'Claim' : 'Add a claim'} onClose={onClose} maxWidth="max-w-3xl">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Title" className="sm:col-span-2"><TextInput value={f.title} onChange={set('title')} /></Field>
        <Field label="Customer"><TextInput value={f.customer} onChange={set('customer')} /></Field>
        <Field label="Project"><TextInput value={f.project} onChange={set('project')} /></Field>
        <Field label="Date"><TextInput type="date" value={f.claim_date} onChange={set('claim_date')} /></Field>
        <Field label="Status"><Select value={f.status} onChange={set('status')} options={statusOptions} /></Field>
        <Field label="Linked invoice"><TextInput value={f.original_invoice_ref} onChange={set('original_invoice_ref')} /></Field>
        {MONEY.map(([k, label]) => <Field key={k} label={`${label} (USD)`}><MoneyInput cents={f[k] === '' ? null : f[k]} onChange={set(k)} /></Field>)}
        <Field label="Reason" className="sm:col-span-2"><TextArea value={f.reason} onChange={set('reason')} /></Field>
        <Field label="Effect on partner fees"><Select value={f.fee_effect} onChange={set('fee_effect')} options={FEE_EFFECT_LABEL} /></Field>
        <Field label="Books deal (for the fee note)"><Select value={f.books_deal_id} onChange={set('books_deal_id')} options={Object.fromEntries((deals || []).map((d) => [d.id, d.name]))} placeholder="None" /></Field>
        <Field label="Fee effect note" className="sm:col-span-2"><TextArea value={f.fee_effect_note} onChange={set('fee_effect_note')} rows={2} /></Field>
        <Field label="Source link" className="sm:col-span-2"><TextInput value={f.source_url} onChange={set('source_url')} /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
      </div>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">The fee effect is recorded here for the partners to decide; it does not change the payout calculator in Books.</p>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button className={btnCls} onClick={save}>{claim ? 'Save' : 'Add'}</button>
        {claim && <button className={btn2Cls} onClick={remove}>Delete</button>}
      </div>
      {claim && <History table="claims" id={claim.id} />}
    </Modal>
  );
}
